import { useState, useEffect, useCallback, useRef } from "react";
import { SlidersHorizontal, Check, CheckCheck, X, ChevronUp, ChevronDown } from "lucide-react";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { fmt, prettyModelId, isUntrackedExternal } from "@/lib/format";
import { KRW_PER_USD, generateBucketKeys, formatBucketLabel, weekStart, monthStart } from "@/lib/timeseries";
import { buildAwsAxis, bedrockActualUsd } from "@/lib/costMatrix";
import ToggleGroup from "@/components/ToggleGroup";
import SectionRangePicker from "@/components/SectionRangePicker";
import CopyButton from "@/components/CopyButton";
import ExportButton from "@/components/ExportButton";
import RowDelta from "@/components/RowDelta";
import ExpenseManager from "@/components/ExpenseManager";
import { cachedCall, invalidateCache } from "@/lib/queryCache";

const GRANULARITY_OPTIONS = [
  { id: "day",   label: "일별" },
  { id: "week",  label: "주별" },
  { id: "month", label: "월별" },
];

// AI 모델 / AWS 인프라 뷰 분리(2026-07-18 피드백 — 모델 7개 + 서비스 8개가 한 매트릭스에
// 있으면 열이 16개라 너무 많다). 기본은 지출의 대부분을 차지하는 AI 모델, 전체 매트릭스는
// "전체" 탭으로 한 번에. 항목 필터는 뷰와 무관하게 세밀 조절용으로 유지.
const KIND_VIEW_OPTIONS = [
  { id: "all",     label: "전체" },
  { id: "model",   label: "AI 모델" },
  { id: "service", label: "AWS 인프라" },
];

// AI 모델(Fable/Sonnet/Opus 등)과 AWS 서비스(Lambda/DynamoDB/WAF 등) 비용을 항목×기간
// 매트릭스로 보여준다. 처음엔 AWS Cost Explorer의 "Tag List" 화면(날짜가 열)을 그대로
// 참고했는데, 대시보드의 다른 표(TrendTable 등)와 마찬가지로 "기간이 행"인 쪽이 이
// 대시보드 안에서 더 일관되고 위에서 아래로 스크롤하는 게 자연스럽다는 피드백(2026-07-18)
// — 날짜를 행(y축), 항목을 열(x축)로 전치했다. 이러면 주/월 단위로 접었을 때 행 개수가
// 확 줄어드는 이점도 있다(항목 수는 열로 고정 폭이라 늘어나도 필터로 대응 가능).
// AI 모델 쪽은 modelCostSeries(부모가 이미 받아둔 timeseries 응답의 ?byModelCost=1 결과,
// 추가 API 호출 없음), 서비스 쪽은 /analytics/expenses의 category=server 항목을
// (service, date)로 묶어서 만든다.
//
// 금액은 원래 통화인 달러(USD)로 그대로 표시한다(2026-07-18 피드백 — AI 원가·AWS 서비스
// 비용 모두 원본 데이터가 달러라, KRW로 환산해서 보여주면 실제 청구서와 숫자가 안 맞아
// 보인다). expenses 는 currency=KRW 로 입력된 항목이 있을 수도 있어 그런 경우만
// KRW_PER_USD 로 역산해 달러로 맞춘다.
//
// 2026-07-28 — AWS 축을 BillingON 실청구로 매핑. 그전까지 AWS 서비스 행은 수기 입력
// (category="server")만으로 만들어져서 사실상 비어 있었다(원장 실사 결과 54행 전부 테스트값).
// 이제 `/analytics/billingon-costs` 의 byServiceDaily(서비스×날짜)를 축으로 쓰고 수기는
// 그 날짜의 정정값으로만 우선한다(규칙·일자별 판정은 lib/costMatrix.js).
// 일/주/월 토글은 일별 셀을 bucketKeyFn 으로 접는 방식이라 추가 API 호출 없이 다 채워진다.
//
// 합계 정의: **실청구 행만** 더한다(BillingON AWS + OpenAI 실지출 + 수기 청구액).
// AI 모델 열(원장 토큰×단가)은 추정치라 지출 합계에 넣지 않는다 — GuidePage "지출" 정의와
// timeseries.js totalExpenseKrw 와 같은 기준이고, 넣으면 Bedrock 실청구와 이중 계상된다.
export default function CostMatrix({ range: initialRange, active }) {
  // 매출·마진 탭의 "비용 세부내역" 세그먼트 본문(2026-07-18) — 예전 접힘 드릴다운에서
  // 승격. 세그먼트에 처음 들어올 때만 조회하고("추이만 보는 사람"에게 불필요한 호출 없음),
  // 다른 세그먼트로 갔다 와도 받아둔 데이터·필터·정렬을 유지한다(hidden 처리).
  const [visited, setVisited] = useState(false);
  // 기간은 탭과 독립(2026-07-18 피드백 — 표/통계마다 독립적으로 조작 가능해야) — 탭
  // 범위를 초기값으로만 쓰고, 여기서 바꾼 기간은 이 매트릭스에만 적용된다. 데이터 조회도
  // 부모에 의존하지 않고 펼쳤을 때 스스로 한다(?byModelCost=1 + 지출 목록).
  const [range, setRange] = useState(initialRange);
  const [expenses, setExpenses] = useState([]);
  const [openaiDays, setOpenaiDays] = useState([]); // OpenAI Costs API 일별 실지출(자동)
  // BillingON 실청구 서비스×날짜 — {서비스:{YYYY-MM-DD:usd}}. AWS 인프라 축의 기본 소스.
  const [awsByServiceDaily, setAwsByServiceDaily] = useState({});
  const [modelCostSeries, setModelCostSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [granularity, setGranularity] = useState("week");
  const [kindView, setKindView] = useState("all"); // "all"(기본) | "model" | "service"
  const [dir, setDir] = useState("desc"); // "desc"(최신순, 기본) | "asc" — TrendTable과 동일한 시각 언어
  const [hidden, setHidden] = useState(new Set()); // 필터에서 끈 항목 key 집합 — 기본은 전부 표시
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 3분 캐시 — 같은 기간을 다시 열면 즉시(2026-07-18 속도 피드백).
      const [expRes, tsRes, oaRes, blRes] = await cachedCall(
        `costs|${range.from}|${range.to}`,
        () => Promise.all([
          analyticsApi.expenses.list({ from: range.from, to: range.to }),
          analyticsApi.timeseries({ from: range.from, to: range.to, granularity: "day", byModelCost: 1 }),
          // 실지출 조회 실패(키 미설정 등)해도 매트릭스는 계속 그린다.
          analyticsApi.openaiCosts.list({ from: range.from, to: range.to }).catch(() => ({ days: [] })),
          // AWS 실청구(BillingON) — 시크릿 미설정·WAF 차단·구버전 Lambda(라우트 404) 어느
          // 경우든 빈 축으로 떨어지고 나머지 행은 그대로 보인다(2026-07-28).
          analyticsApi.billingonCosts.list({ from: range.from, to: range.to })
            .catch(() => ({ byServiceDaily: {} })),
        ])
      );
      setExpenses(expRes.expenses || []);
      setModelCostSeries(tsRes.modelCostSeries || []);
      setOpenaiDays(oaRes.days || []);
      setAwsByServiceDaily(blRes.byServiceDaily || {});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    if (active) setVisited(true);
  }, [active]);

  // 세그먼트에 들어오기 전엔 조회를 미룬다 — 들어온 순간(및 그 뒤 기간이 바뀔 때만) 호출.
  useEffect(() => {
    if (visited) fetchAll();
  }, [visited, fetchAll]);

  useEffect(() => {
    if (!filterOpen) return;
    function onClickOutside(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [filterOpen]);

  const bucketKeyFn = granularity === "week" ? weekStart : granularity === "month" ? monthStart : (d) => d;
  const periodsAsc = generateBucketKeys(range.from, range.to, granularity);
  const periods = dir === "desc" ? [...periodsAsc].reverse() : periodsAsc;

  const modelRowsMap = new Map();
  for (const bucket of modelCostSeries || []) {
    const col = bucketKeyFn(bucket.bucket);
    for (const [modelId, usd] of Object.entries(bucket.byModel || {})) {
      if (!modelRowsMap.has(modelId)) modelRowsMap.set(modelId, new Map());
      const m = modelRowsMap.get(modelId);
      m.set(col, (m.get(col) || 0) + usd);
    }
  }

  const serviceRowsMap = new Map();
  // OpenAI 실지출(자동) — 청구 API 기준. AI 지출 성격이라 'AI 모델'·'전체' 탭에 노출
  // (AWS 인프라 탭에선 제외 — AWS 비용이 아님).
  const openaiRowsMap = new Map();
  const OPENAI_ROW = "OpenAI API (실지출·자동)";
  for (const d of openaiDays) {
    if (!d?.usd) continue;
    const col = bucketKeyFn(d.date);
    if (!openaiRowsMap.has(OPENAI_ROW)) openaiRowsMap.set(OPENAI_ROW, new Map());
    const m = openaiRowsMap.get(OPENAI_ROW);
    m.set(col, (m.get(col) || 0) + d.usd);
  }
  // AWS 인프라 축 — 자동(BillingON 실청구) 기본 + 그 날짜에 수기 server 입력이 있으면 수기
  // 우선(일자별 판정 → 주/월로 접어도 하루 수기가 그 주 자동값을 덮지 않는다). 규칙·테스트는
  // lib/costMatrix.js. 수기 category="ai"(AI 구독·API료)는 AWS 청구가 아니라 별도 행으로 둔다.
  const serverExpenses = [];
  for (const e of expenses) {
    // server = AWS 인프라, ai = AI 구독·API료(2026-07-21 카테고리 추가) — 둘 다 매트릭스
    // 행으로. 인건비/마케팅비는 서비스 지출 성격이 아니라 매트릭스에선 제외(추이 탭 지출엔 포함).
    if (e.category !== "server" && e.category !== "ai") continue;
    const amountUsd = e.currency === "USD" ? (e.amount || 0) : (e.amount || 0) / KRW_PER_USD;
    if (e.category === "server") {
      serverExpenses.push({ date: e.date, service: e.service, amountUsd });
      continue;
    }
    const key = e.service || "AI 구독·API료";
    const col = bucketKeyFn(e.date);
    if (!serviceRowsMap.has(key)) serviceRowsMap.set(key, new Map());
    const m = serviceRowsMap.get(key);
    m.set(col, (m.get(col) || 0) + amountUsd);
  }
  const aws = buildAwsAxis({ byServiceDaily: awsByServiceDaily, serverExpenses, bucketKeyFn });
  const awsRowsMap = aws.cells;

  function toRows(map, kind) {
    return [...map.entries()].map(([label, byCol]) => {
      const total = [...byCol.values()].reduce((s, v) => s + v, 0);
      return {
        key: `${kind}:${label}`,
        // AI 모델 행은 원장 토큰×단가 추정치 — 실청구 행과 한 표에 섞여 있으면 구분이 안 돼
        // 라벨에 명시한다(합계에서도 제외된다).
        // 추정 중에서도 Exa 는 대조할 실청구 소스 자체가 없다(2026-08-14). "(추정)" 하나로
        // 묶으면 BillingON 과 맞춰볼 수 있는 Bedrock·OpenAI 추정과 구별이 안 되므로 따로 쓴다.
        label:
          kind !== "model"
            ? label
            : isUntrackedExternal(label)
              ? `${prettyModelId(label)} (추정·실청구 미연동)`
              : `${prettyModelId(label)} (추정)`,
        byCol,
        total,
      };
    });
  }

  const allRows = [
    ...toRows(modelRowsMap, "model"),
    ...toRows(openaiRowsMap, "openai"),
    ...toRows(awsRowsMap, "aws"),
    ...toRows(serviceRowsMap, "service"),
  ].sort((a, b) => b.total - a.total);
  const kindRows = allRows.filter((r) =>
    kindView === "all" ||
    r.key.startsWith(`${kindView}:`) ||
    (r.key.startsWith("openai:") && kindView === "model") || // OpenAI 실지출은 AI 모델 탭에도
    // AWS 실청구(자동)와 수기 청구액은 둘 다 인프라비 성격 → 'AWS 인프라' 탭에 함께.
    ((r.key.startsWith("aws:") || r.key.startsWith("service:")) && kindView === "service")
  );
  const rows = kindRows.filter((r) => !hidden.has(r.key));
  // 합계는 실청구 행만 — AI 모델(추정) 열은 참고 지표라 더하지 않는다. 더하면 BillingON 의
  // Bedrock 실청구와 이중 계상된다(2026-07-28).
  const actualRows = rows.filter((r) => !r.key.startsWith("model:"));
  const grandTotal = actualRows.reduce((s, r) => s + r.total, 0);
  const periodTotals = periods.map((p) => actualRows.reduce((s, r) => s + (r.byCol.get(p) || 0), 0));
  const estimateShown = rows.length !== actualRows.length;
  // 추정 행 중 실청구와 대조할 소스가 아예 없는 몫(현재 Exa). 합계에는 원래 안 들어가지만,
  // 얼마가 "확인 불가 구간"인지는 각주로 밝힌다 — 규모가 작다고 성격이 같아지진 않는다.
  const untrackedRows = rows.filter(
    (r) => r.key.startsWith("model:") && isUntrackedExternal(r.key.slice("model:".length))
  );
  const untrackedUsd = untrackedRows.reduce((s, r) => s + r.total, 0);
  // 직전 구간 대비 합계 증감(2026-07-18) — 시간순(periodsAsc) 기준 이전 구간과 비교.
  const totalByPeriod = new Map(periods.map((p, i) => [p, periodTotals[i]]));
  const prevPeriodOf = new Map();
  periodsAsc.forEach((p, i) => {
    if (i > 0) prevPeriodOf.set(p, periodsAsc[i - 1]);
  });

  function toggleRow(key) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const filterCount = hidden.size;

  // 복사/CSV용 — 화면과 같은 구조(기간=행, 항목=열), 항목 필터·정렬 방향 반영. USD 소수 2자리.
  const usd2 = (v) => Math.round((v || 0) * 100) / 100;
  const exportRows = [
    ["기간", ...rows.map((r) => `${r.label}(USD)`), "합계(USD)"],
    ["합계", ...rows.map((r) => usd2(r.total)), usd2(grandTotal)],
    ...periods.map((p, i) => [
      formatBucketLabel(p, granularity),
      ...rows.map((r) => usd2(r.byCol.get(p))),
      usd2(periodTotals[i]),
    ]),
  ];

  return (
    <div className={active ? "animate-fade-in" : "hidden"}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          지출 세부내역 <span className="font-normal normal-case text-ink-400">— AI 모델·AWS 서비스 × 기간 · USD</span>
          {(() => {
            // 대조: OpenAI 실청구 vs 우리 추정(gpt* 모델 토큰×단가 + 이미지 생성 정액 원가는 추정에 미포함).
            const actual = openaiDays.reduce((s, d) => s + (d.usd || 0), 0);
            if (actual <= 0) return null;
            const est = [...modelRowsMap.entries()]
              .filter(([id]) => id.startsWith("gpt"))
              .reduce((s, [, byCol]) => s + [...byCol.values()].reduce((a, b) => a + b, 0), 0);
            const diff = est > 0 ? Math.round(((actual - est) / est) * 100) : null;
            return (
              <span className="ml-2 font-normal normal-case text-ink-400">
                · OpenAI 실지출 ${(Math.round(actual * 100) / 100).toLocaleString()}
                {est > 0 && ` vs GPT 추정 $${(Math.round(est * 100) / 100).toLocaleString()}${diff !== null ? ` (${diff >= 0 ? "+" : ""}${diff}%)` : ""}`}
              </span>
            );
          })()}
          {(() => {
            // 같은 대조의 AWS 판(2026-07-28) — BillingON Bedrock 실청구 vs 원장 토큰×단가 추정
            // (gpt* 제외 = Bedrock 계열). FinOps 월간 대사 1번 항목(±5%)을 화면에서 바로 본다.
            const actual = bedrockActualUsd(awsByServiceDaily);
            if (actual <= 0) return null;
            const est = [...modelRowsMap.entries()]
              .filter(([id]) => !id.startsWith("gpt"))
              .reduce((s, [, byCol]) => s + [...byCol.values()].reduce((a, b) => a + b, 0), 0);
            const diff = est > 0 ? Math.round(((actual - est) / est) * 100) : null;
            return (
              <span className="ml-2 font-normal normal-case text-ink-400">
                · Bedrock 실청구 ${(Math.round(actual * 100) / 100).toLocaleString()}
                {est > 0 && ` vs 원장 추정 $${(Math.round(est * 100) / 100).toLocaleString()}${diff !== null ? ` (${diff >= 0 ? "+" : ""}${diff}%)` : ""}`}
              </span>
            );
          })()}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup options={KIND_VIEW_OPTIONS} value={kindView} onChange={setKindView} />
          <div className="relative" ref={filterRef}>
            <button
              type="button"
              onClick={() => setFilterOpen((o) => !o)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
                filterCount > 0 ? "bg-brand-50 text-brand-700" : "bg-ink-50 text-ink-600 hover:bg-ink-100"
              }`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              항목 필터
              {filterCount > 0 && (
                <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {allRows.length - filterCount}/{allRows.length}
                </span>
              )}
            </button>
            {filterOpen && (
              // left-0 앵커(2026-07-18) — 이 버튼은 컨트롤 줄의 왼쪽 끝이라 right-0 으로
              // 열면 팝오버가 화면 왼쪽 밖으로 잘려 나감. 뷰포트 밖 방지용 max-w 도 함께.
              <div className="absolute left-0 top-full z-20 mt-1.5 max-h-80 w-72 max-w-[calc(100vw-2.5rem)] overflow-y-auto rounded-xl border border-ink-100 bg-white p-1.5 shadow-[0_4px_12px_rgba(15,23,42,0.12)]">
                {/* 전체 선택/해제는 스크롤해도 위에 고정 — 긴 목록에서도 한 번에 리셋 가능 */}
                <div className="sticky -top-1.5 z-10 -mx-1.5 mb-1 flex items-center gap-1 border-b border-ink-100 bg-white px-1.5 pb-1 pt-1.5">
                  <button
                    type="button"
                    onClick={() => setHidden(new Set())}
                    className="flex flex-1 items-center gap-1.5 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-ink-500 hover:bg-ink-50"
                  >
                    <CheckCheck className="h-3.5 w-3.5" />
                    전체 선택
                  </button>
                  <button
                    type="button"
                    onClick={() => setHidden(new Set(allRows.map((r) => r.key)))}
                    className="flex flex-1 items-center gap-1.5 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-ink-500 hover:bg-ink-50"
                  >
                    <X className="h-3.5 w-3.5" />
                    전체 해제
                  </button>
                </div>
                {/* AI 모델 / AWS 실청구 / 수기 입력으로 묶고 항목별 기간 합계를 같이 보여준다
                    (2026-07-18, 2026-07-28 실청구 그룹 추가) — 한 덩어리로 섞여 있으면 뭐가
                    모델이고 뭐가 인프라인지 스캔이 안 되고, 금액이 있어야 "끌지 말지"를 판단할
                    수 있다는 피드백. 섹션마다 색 점 + 구분선 + 옅은 헤더 배경으로 경계를 분명히.
                    보라=AI 모델(추정), 주황=AWS 실청구(BillingON), 청록=OpenAI 실지출,
                    회색=수기 입력. */}
                {[
                  ["model", "AI 모델 (추정·참고)", "#8b5cf6"],
                  ["aws", "AWS 인프라 (실청구·자동 / 수기 정정)", "#f59e0b"],
                  ["openai", "OpenAI 실지출 (자동)", "#14b8a6"],
                  ["service", "수기 입력 (AI 구독·API료)", "#64748b"],
                ].map(([kind, title, color], gi) => {
                  const group = allRows.filter((r) => r.key.startsWith(`${kind}:`));
                  if (group.length === 0) return null;
                  return (
                    <div key={kind} className={gi > 0 ? "mt-1.5 border-t border-ink-100 pt-1.5" : ""}>
                      <div className="mb-0.5 flex items-center gap-1.5 rounded-md bg-ink-50 px-2.5 py-1.5 text-[11px] font-semibold text-ink-600">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                        {title}
                        <span className="font-normal text-ink-400">{group.length}개</span>
                      </div>
                      {group.map((r) => {
                        const rowActive = !hidden.has(r.key);
                        return (
                          <button
                            key={r.key}
                            type="button"
                            onClick={() => toggleRow(r.key)}
                            className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm ${
                              rowActive ? "bg-brand-50 text-brand-700" : "text-ink-600 hover:bg-ink-50"
                            }`}
                          >
                            <span className="flex-1 truncate">{r.label}</span>
                            <span className={`shrink-0 text-xs tabular-nums ${rowActive ? "text-brand-600" : "text-ink-400"}`}>
                              {fmt.usd(r.total)}
                            </span>
                            <span className="w-3.5 shrink-0">{rowActive && <Check className="h-3.5 w-3.5" />}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <SectionRangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
          <ToggleGroup options={GRANULARITY_OPTIONS} value={granularity} onChange={setGranularity} />
          <CopyButton rows={exportRows} />
          <ExportButton filename={kindView === "all" ? "costs" : `costs-${kindView}`} rows={exportRows} />
        </div>
      </div>

      {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {!error && loading && rows.length === 0 && <div className="text-sm text-ink-400">불러오는 중…</div>}
      {!error && !loading && kindRows.length === 0 && (
        <div className="text-sm text-ink-400">이 기간에 이 뷰에 해당하는 비용이 없습니다.</div>
      )}
      {!error && !loading && kindRows.length > 0 && rows.length === 0 && (
        <div className="text-sm text-ink-400">필터에서 전부 해제됐습니다 — "항목 필터"에서 다시 선택해 주세요.</div>
      )}
      {!error && rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-ink-100">
          <table className="text-sm">
            <thead className="bg-ink-50">
              <tr>
                <th className="sticky left-0 z-10 whitespace-nowrap bg-ink-50 px-3 py-2 text-left text-xs font-medium text-ink-500">
                  <button
                    type="button"
                    onClick={() => setDir((d) => (d === "desc" ? "asc" : "desc"))}
                    className="inline-flex items-center gap-1 hover:text-ink-800"
                  >
                    기간
                    {dir === "desc" ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
                  </button>
                </th>
                {rows.map((r) => (
                  <th key={r.key} className="whitespace-nowrap px-3 py-2 text-right text-xs font-medium text-ink-500">
                    {r.label}
                  </th>
                ))}
                <th
                  className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold text-ink-700"
                  title="실청구 행만 더한 값 — AI 모델(추정) 열은 제외"
                >
                  합계{estimateShown ? " (실청구)" : ""}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {/* 합계는 표 맨 위 — 스크롤 없이 총량부터 보이게(2026-07-18 피드백). */}
              <tr className="border-b-2 border-ink-200 bg-ink-50 font-semibold">
                <td className="sticky left-0 z-10 whitespace-nowrap bg-ink-50 px-3 py-2 text-ink-900">합계</td>
                {rows.map((r) => (
                  <td key={r.key} className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-900">{fmt.usd(r.total)}</td>
                ))}
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-900">{fmt.usd(grandTotal)}</td>
              </tr>
              {periods.map((p, i) => (
                <tr key={p} className="hover:bg-ink-50">
                  <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-2 font-medium text-ink-900">
                    {formatBucketLabel(p, granularity)}
                  </td>
                  {rows.map((r) => (
                    <td key={r.key} className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-500">
                      {fmt.usd(r.byCol.get(p) || 0)}
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums text-ink-900">
                    {fmt.usd(periodTotals[i])}
                    <RowDelta
                      cur={periodTotals[i]}
                      prev={prevPeriodOf.has(p) ? totalByPeriod.get(prevPeriodOf.get(p)) : null}
                      format={(v) => fmt.usd(v)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!error && rows.length > 0 && (
        <p className="mt-2 text-[11px] leading-relaxed text-ink-400">
          AWS 축은 BillingON 실청구(자동)이고, 같은 날짜에 수기 서버비 입력이 있으면 그 날짜는 수기가 우선합니다.
          {aws.overriddenAutoUsd > 0 &&
            ` 이 기간엔 수기 입력이 있는 날짜의 자동값 $${(Math.round(aws.overriddenAutoUsd * 100) / 100).toLocaleString()}이 수기값으로 대체됐습니다.`}
          {" "}청구 데이터는 1~2일 지연 확정이라 마지막 기간은 과소 표시될 수 있습니다.
          {estimateShown && " 합계는 실청구 행만 더한 값입니다 — AI 모델(추정) 열은 참고 지표라 합계에서 제외됩니다(Bedrock 실청구와 이중 계상 방지)."}
          {untrackedRows.length > 0 &&
            ` "(추정·실청구 미연동)" 행은 청구 데이터를 끌어올 경로가 없어 대조가 불가능한 추정치입니다`
            + ` — 이 기간 $${(Math.round(untrackedUsd * 100) / 100).toLocaleString()}.`
            + " 나머지 추정 행은 BillingON·OpenAI 실청구와 맞춰볼 수 있습니다."}
        </p>
      )}

      {/* 지출 수기 입력·관리(2026-07-21) — 등록/삭제 후 이 매트릭스의 캐시(costs|…)를
          비우고 재조회해 방금 넣은 청구액이 바로 반영되게 한다. 추이 탭 지출/순이익은
          별도 쿼리 캐시(useAnalyticsQuery)라 최대 3분 내 자동 갱신. */}
      <ExpenseManager
        range={range}
        onChanged={() => {
          invalidateCache("costs|");
          fetchAll();
        }}
      />
    </div>
  );
}
